// src/contexts/WebSocketContext.js
import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { useNotifications } from './NotificationContext';

const WebSocketContext = createContext();

export const WebSocketProvider = ({ children }) => {
  const wsBaseUrl = process.env.REACT_APP_WS_BASE_URL;
  const { fetchNotifications, playNotificationSound } = useNotifications();

  // Track current publication ID for filtering WebSocket messages
  const currentPublicationIdRef = useRef(null);

  // Global WebSocket connection for all user tasks
  const globalWsConnection = useRef(null);
  const globalWsReconnectTimeout = useRef(null);

  // Error callback refs for each task type
  const errorCallbacks = useRef({
    video: {},
    ppt: {},
    poster: {},
    audio: {},
    carousel: {},
    instapost: {},
    images: {},
  });

  // WebSocket connections. Now nested by project/task ID to allow concurrent local sockets
  const wsConnections = useRef({
    video: {},
    ppt: {},
    poster: {},
    audio: {},
    carousel: {},
    instapost: {},
    images: {},
  });

  // Active tasks state. Now arrays of active Project IDs.
  const [activeTasksState, setActiveTasksState] = useState({
    video: [],
    ppt: [],
    poster: [],
    audio: [],
    carousel: [],
    instapost: [],
    images: [],
  });

  // Wrapper to handle legacy boolean updates (true/false) from older handler code
  const setActiveTasks = (updater) => {
    setActiveTasksState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const result = { ...prev };
      for (const key of Object.keys(next)) {
        if (next[key] === true) {
          result[key] = prev[key] && prev[key].length ? prev[key] : ['legacy'];
        } else if (next[key] === false) {
          result[key] = [];
        } else if (Array.isArray(next[key])) {
          result[key] = next[key];
        }
      }
      return result;
    });
  };

  // Task status tracking. Now nested dictionaries keyed by Project ID.
  const [taskStatus, setTaskStatus] = useState({
    video: {},
    ppt: {},
    poster: {},
    audio: {},
    carousel: {},
    instapost: {},
    images: {},
  });

  // Update localStorage when activeTasks changes
  useEffect(() => {
    const hasActiveTasks = Object.values(activeTasksState).some((arr) => Array.isArray(arr) && arr.length > 0);
    localStorage.setItem("activeTasks", hasActiveTasks ? "true" : "false");
  }, [activeTasksState]);

  // Setup local WebSocket connection
  const setupWebSocket = (taskId, taskType, publicationId, projectId = null, onError = null) => {
    // Update current publication ID
    currentPublicationIdRef.current = publicationId;

    // Use projectId as unique key, fallback to taskId
    const uniqueId = projectId || taskId;

    // Initialize if not present
    if (!wsConnections.current[taskType]) {
      wsConnections.current[taskType] = {};
    }
    if (!errorCallbacks.current[taskType]) {
      errorCallbacks.current[taskType] = {};
    }

    // Store error callback for this task type and uniqueId
    if (onError) {
      errorCallbacks.current[taskType][uniqueId] = onError;
    }

    // Close existing connection for this specific project if any
    if (wsConnections.current[taskType][uniqueId]) {
      wsConnections.current[taskType][uniqueId].close();
    }

    // Initialize the dictionary entry immediately
    setTaskStatus(prev => ({
      ...prev,
      [taskType]: {
        ...(prev[taskType] || {}),
        [uniqueId]: {
          ...((prev[taskType] || {})[uniqueId] || {}),
          progress: 0,
          status: "queued",
          publicationId: publicationId,
          pptId: taskType.includes('ppt') ? projectId : null,
          video_project_id: taskType.includes('video') ? projectId : null,
          posterId: taskType.includes('poster') ? projectId : null,
          audioId: taskType.includes('audio') ? projectId : null,
        }
      }
    }));

    const wsUrl = `${wsBaseUrl}/taskStatus/${taskId}`;
    wsConnections.current[taskType][uniqueId] = new WebSocket(wsUrl);
    console.log(`WebSocket connection established for ${taskType} [${uniqueId}]:`, wsUrl);

    wsConnections.current[taskType][uniqueId].onmessage = (event) => {
      console.log(`WebSocket message received for ${taskType} [${uniqueId}]:`, event.data);
      const data = JSON.parse(event.data);

      if (data.publicationId && currentPublicationIdRef.current &&
        Number(data.publicationId) !== Number(currentPublicationIdRef.current)) {
        console.log(`WebSocket message for different publication - ignoring`);
        return;
      }

      if (data.error && (data.error.includes("HTTP error: 404") || data.error.includes("HTTP error: 500"))) {
        console.log(`WebSocket received HTTP error for: ${taskType} [${uniqueId}] - Disconnecting`);
        if (wsConnections.current[taskType][uniqueId]) {
          const ws = wsConnections.current[taskType][uniqueId];
          wsConnections.current[taskType][uniqueId] = null;
          ws.close();
        }
        setActiveTasks(prev => ({
          ...prev,
          [taskType]: (prev[taskType] || []).filter(id => id !== uniqueId)
        }));

        setTaskStatus(prev => ({
          ...prev,
          [taskType]: {
            ...(prev[taskType] || {}),
            [uniqueId]: {
              ...(prev[taskType]?.[uniqueId] || {}),
              progress: Math.max(prev[taskType]?.[uniqueId]?.progress || 0, 0),
              status: "error",
            }
          }
        }));
        if (errorCallbacks.current[taskType]?.[uniqueId]) {
          errorCallbacks.current[taskType][uniqueId]("Oops! Something went wrong on our end.", "error");
        }
        return;
      }

      if (data.progress < 0) {
        console.log(`WebSocket received negative progress for: ${taskType} [${uniqueId}] - Disconnecting`);
        if (wsConnections.current[taskType][uniqueId]) {
          const ws = wsConnections.current[taskType][uniqueId];
          wsConnections.current[taskType][uniqueId] = null;
          ws.close();
        }
        setActiveTasks(prev => ({
          ...prev,
          [taskType]: (prev[taskType] || []).filter(id => id !== uniqueId)
        }));
        setTaskStatus(prev => ({
          ...prev,
          [taskType]: {
            ...(prev[taskType] || {}),
            [uniqueId]: {
              ...(prev[taskType]?.[uniqueId] || {}),
              progress: Math.max(prev[taskType]?.[uniqueId]?.progress || 0, 0),
              status: "failed",
            }
          }
        }));
        if (errorCallbacks.current[taskType]?.[uniqueId]) {
          errorCallbacks.current[taskType][uniqueId]("Oops! Something went wrong on our end.", "error");
        }
        return;
      }

      if (!wsConnections.current[taskType][uniqueId]) {
        return;
      }

      const safeProgress = Math.max(data.progress || 0, 0);
      const normalizeLocalStatus = (s) => {
        if (!s) return "";
        const lower = s.toLowerCase().replace(/[_\s]/g, "");
        if (lower === "inprogress") return "generating";
        if (lower === "queued") return "queued";
        if (lower === "completed") return "completed";
        if (lower === "failed" || lower === "terminated" || lower === "error" || lower === "cancelled") return "failed";
        if (lower === "processing") return "processing";
        if (lower === "generating") return "generating";
        if (lower === "merging") return "merging";
        if (lower === "templatesselected" || s === "TemplatesSelected") return "TemplatesSelected";
        return s;
      };
      let normalizedLocalStatus = normalizeLocalStatus(data.status);
      if (normalizedLocalStatus === "queued" && safeProgress === 100) {
        normalizedLocalStatus = "TemplatesSelected";
      }

      const incomingPptId = data?.pptId || data?.ppt_id || (taskType.includes('ppt') ? data?.project_id : null);
      const incomingVideoId = data?.video_project_id || data?.videoId || (taskType.includes('video') ? data?.project_id : null);
      const incomingPosterId = data?.posterId || data?.poster_id || (taskType.includes('poster') ? data?.project_id : null);
      const incomingAudioId = data?.audioId || data?.audio_id || (taskType.includes('audio') ? data?.project_id : null);
      const incomingCarouselId = data?.carouselId || data?.carousel_id || (taskType.includes('carousel') ? data?.project_id : null);
      const incomingInstapostId = data?.instapostId || data?.instapost_id || (taskType.includes('instapost') || taskType.includes('post') ? data?.project_id : null);

      // Update taskStatus
      setTaskStatus((prev) => ({
        ...prev,
        [taskType]: {
          ...(prev[taskType] || {}),
          [uniqueId]: {
            ...(prev[taskType]?.[uniqueId] || {}),
            progress: safeProgress,
            status: normalizedLocalStatus,
            fileId: data.fileId || prev[taskType]?.[uniqueId]?.fileId,
            publicationId: prev[taskType]?.[uniqueId]?.publicationId || publicationId || null,
            pptId: incomingPptId || prev[taskType]?.[uniqueId]?.pptId || null,
            video_project_id: incomingVideoId || prev[taskType]?.[uniqueId]?.video_project_id || null,
            posterId: incomingPosterId || prev[taskType]?.[uniqueId]?.posterId || null,
            audioId: incomingAudioId || prev[taskType]?.[uniqueId]?.audioId || null,
            carouselId: incomingCarouselId || prev[taskType]?.[uniqueId]?.carouselId || null,
            instapostId: incomingInstapostId || prev[taskType]?.[uniqueId]?.instapostId || null,
            project_id: data?.project_id || prev[taskType]?.[uniqueId]?.project_id || null,
          }
        },
      }));

      // Update activeTasks
      if (safeProgress > 0 && safeProgress < 100 && normalizedLocalStatus !== "failed" && normalizedLocalStatus !== "error") {
        if (errorCallbacks.current[taskType]?.[uniqueId]) {
          delete errorCallbacks.current[taskType][uniqueId];
        }
        setActiveTasks(prev => {
          const currentArr = prev[taskType] || [];
          if (!currentArr.includes(uniqueId)) {
            return { ...prev, [taskType]: [...currentArr, uniqueId] };
          }
          return prev;
        });
      }

      if (normalizedLocalStatus === "completed" || normalizedLocalStatus === "TemplatesSelected" || (safeProgress === 100 && data.fileId)) {
        setActiveTasks(prev => ({
          ...prev,
          [taskType]: (prev[taskType] || []).filter(id => id !== uniqueId)
        }));
        setTaskStatus(prev => ({
          ...prev,
          [taskType]: {
            ...(prev[taskType] || {}),
            [uniqueId]: {
              ...(prev[taskType]?.[uniqueId] || {}),
              progress: 100,
              status: "completed",
              fileId: data.fileId || prev[taskType]?.[uniqueId]?.fileId,
              publicationId: prev[taskType]?.[uniqueId]?.publicationId || publicationId || null,
            }
          }
        }));

        if (wsConnections.current[taskType][uniqueId]) {
          wsConnections.current[taskType][uniqueId].close();
          console.log(`WebSocket closed after completion: ${taskType} [${uniqueId}]`);
        }
      }
    };

    wsConnections.current[taskType][uniqueId].onerror = (error) => {
      console.error(`${taskType} [${uniqueId}] WebSocket error:`, error);
      // We rely on the global WS and explicit data.error messages from the backend 
      // instead of local WS connection errors to prevent false positive notifications.
    };

    wsConnections.current[taskType][uniqueId].onclose = (event) => {
      console.log(`${taskType} [${uniqueId}] WebSocket closed`, event);
      if (wsConnections.current[taskType][uniqueId] !== null) {
        setActiveTasks(prev => ({
          ...prev,
          [taskType]: (prev[taskType] || []).filter(id => id !== uniqueId)
        }));
      }
    };
  };

  useEffect(() => {
    const wsConnectionsRef = wsConnections.current;
    return () => {
      Object.values(wsConnectionsRef).forEach((typeGroup) => {
        Object.values(typeGroup).forEach(ws => {
          if (ws && ws.close) ws.close();
        });
      });
      if (globalWsConnection.current) {
        globalWsConnection.current.close();
      }
      if (globalWsReconnectTimeout.current) {
        clearTimeout(globalWsReconnectTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        if (user.userId) {
          console.log("[WebSocket] Auto-connecting to global WebSocket with userId:", user.userId);
          connectGlobalWebSocket(user.userId);
        }
      } catch (error) {
        console.error("[WebSocket] Error parsing userData:", error);
      }
    }

    return () => {
      disconnectGlobalWebSocket();
    };
  }, []);

  const connectGlobalWebSocket = (userId) => {
    if (globalWsConnection.current) {
      globalWsConnection.current.close();
      globalWsConnection.current = null;
    }

    if (globalWsReconnectTimeout.current) {
      clearTimeout(globalWsReconnectTimeout.current);
      globalWsReconnectTimeout.current = null;
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//plan.pubvision.io/ws/${userId}`;
    console.log("[Global WebSocket] Connecting to:", wsUrl);

    try {
      globalWsConnection.current = new WebSocket(wsUrl);

      globalWsConnection.current.onopen = () => {
        console.log("[Global WebSocket] Connected successfully");
      };

      globalWsConnection.current.onmessage = (event) => {
        // console.log("[Global WebSocket] Message received:", event.data);
        try {
          const message = JSON.parse(event.data);
          const { type, task_type, task_id, progress, status, data, timestamp } = message;

          if (type === "connection_established") {
            return;
          }

          const resolvedTaskType = (type === "task_progress" ? task_type : type) || "";
          const publicationId = data?.publication_id;

          const normalizeBackendStatus = (s) => {
            if (!s) return "";
            const lower = s.toLowerCase().replace(/[_\s]/g, "");
            if (lower === "inprogress") return "generating";
            if (lower === "queued") return "queued";
            if (lower === "completed") return "completed";
            if (lower === "failed" || lower === "terminated" || lower === "error" || lower === "cancelled") return "failed";
            if (lower === "processing") return "processing";
            if (lower === "generating") return "generating";
            if (lower === "merging") return "merging";
            if (lower === "templatesselected" || s === "TemplatesSelected") return "TemplatesSelected";
            return s;
          };
          let normalizedStatus = normalizeBackendStatus(status);
          const safeProgress = Math.max(progress || 0, 0);
          if (progress < 0) {
            normalizedStatus = "failed";
          } else if (normalizedStatus === "queued" && safeProgress === 100) {
            normalizedStatus = "TemplatesSelected";
          }

          if (resolvedTaskType && publicationId) {
            const taskType = resolvedTaskType.toLowerCase();

            const incomingPptId = data?.pptId || data?.ppt_id || (taskType.includes('ppt') ? data?.project_id : null);
            const incomingVideoId = data?.video_project_id || data?.videoId || (taskType.includes('video') ? data?.project_id : null);
            const incomingPosterId = data?.posterId || data?.poster_id || (taskType.includes('poster') ? data?.project_id : null);
            const incomingAudioId = data?.audioId || data?.audio_id || (taskType.includes('audio') ? data?.project_id : null);
            const incomingCarouselId = data?.carouselId || data?.carousel_id || (taskType.includes('carousel') ? data?.project_id : null);
            const incomingInstapostId = data?.instapostId || data?.instapost_id || (taskType.includes('instapost') || taskType.includes('post') ? data?.project_id : null);

            let uniqueId = incomingPptId || incomingVideoId || incomingPosterId || incomingAudioId || incomingCarouselId || incomingInstapostId || task_id || data?.taskId || 'unknown';

            const isInitialPush = type === "task_progress";
            const isIncomingTerminal = safeProgress >= 100 || normalizedStatus === "completed" || normalizedStatus === "failed" || normalizedStatus === "error";

            console.log(`[Global WebSocket] taskType=${taskType} status=${normalizedStatus} progress=${safeProgress} uniqueId=${uniqueId}`);

            // ── State model ──────────────────────────────────────────────────────────
            // ACTIVE  (keep in activeTasks): queued, generating, merging
            // PASSIVE (remove from activeTasks — DB is source of truth):
            //         TemplatesSelected, processing, completed, failed
            // ─────────────────────────────────────────────────────────────────────
            const isPassiveState =
              normalizedStatus === "TemplatesSelected" ||
              normalizedStatus === "processing" ||
              normalizedStatus === "completed" ||
              normalizedStatus === "failed" ||
              safeProgress >= 100 ||
              progress < 0 ||
              status === "error";

            if (isPassiveState) {
              // Remove from activeTasks — DB will be queried for the current status.
              setActiveTasks((a) => {
                const nextArr = (a[taskType] || []).filter(id => String(id) !== String(uniqueId));
                const nextState = { ...a, [taskType]: nextArr };
                
                // Clean up any stale manually-injected base tasks
                const baseTaskType = taskType === "video_templates_llm" ? "video" : (taskType === "templates_llm" ? "ppt" : null);
                if (baseTaskType && nextState[baseTaskType]) {
                  nextState[baseTaskType] = nextState[baseTaskType].filter(id => String(id) !== String(uniqueId) && String(id) !== "pending");
                }
                
                return nextState;
              });
              if (
                normalizedStatus === "completed" ||
                normalizedStatus === "TemplatesSelected" ||
                safeProgress >= 100
              ) {
                if (fetchNotifications) fetchNotifications();
              }
            } else {
              // Active state (queued / generating / merging / in-progress) — keep in activeTasks.
              setActiveTasks((a) => {
                const nextState = { ...a };
                const currentArr = (nextState[taskType] || []).map(String);
                
                if (!currentArr.includes(String(uniqueId))) {
                  nextState[taskType] = [...(nextState[taskType] || []), uniqueId];
                }
                
                // If backend is actively sending extraction events, clear the manually-injected base task
                const baseTaskType = taskType === "video_templates_llm" ? "video" : (taskType === "templates_llm" ? "ppt" : null);
                if (baseTaskType && nextState[baseTaskType]) {
                  nextState[baseTaskType] = nextState[baseTaskType].filter(id => String(id) !== String(uniqueId) && String(id) !== "pending");
                }
                
                return nextState;
              });
            }

            setTaskStatus((prev) => ({
              ...prev,
              [taskType]: {
                ...prev[taskType],
                [uniqueId]: {
                  ...(prev[taskType]?.[uniqueId] || {}),
                  progress: safeProgress,
                  status: normalizedStatus,
                  publicationId: publicationId || prev[taskType]?.[uniqueId]?.publicationId,
                  taskId: task_id,
                  timestamp: timestamp,
                  pptId: incomingPptId || prev[taskType]?.[uniqueId]?.pptId || null,
                  video_project_id: incomingVideoId || prev[taskType]?.[uniqueId]?.video_project_id || null,
                  posterId: incomingPosterId || prev[taskType]?.[uniqueId]?.posterId || null,
                  audioId: incomingAudioId || prev[taskType]?.[uniqueId]?.audioId || null,
                  carouselId: incomingCarouselId || prev[taskType]?.[uniqueId]?.carouselId || null,
                  instapostId: incomingInstapostId || prev[taskType]?.[uniqueId]?.instapostId || null,
                  fileId: data?.fileId || prev[taskType]?.[uniqueId]?.fileId || null,
                }
              },
            }));
          }
        } catch (error) {
          console.error("[Global WebSocket] Error parsing message:", error);
        }
      };

      globalWsConnection.current.onerror = (error) => {
        console.error("[Global WebSocket] Error:", error);
      };

      globalWsConnection.current.onclose = (event) => {
        console.log("[Global WebSocket] Connection closed", event);
        if (!event.wasClean && userId) {
          console.log("[Global WebSocket] Attempting to reconnect in 5 seconds...");
          globalWsReconnectTimeout.current = setTimeout(() => {
            connectGlobalWebSocket(userId);
          }, 5000);
        }
      };
    } catch (error) {
      console.error("[Global WebSocket] Connection error:", error);
    }
  };

  const disconnectGlobalWebSocket = () => {
    if (globalWsConnection.current) {
      console.log("[Global WebSocket] Disconnecting...");
      globalWsConnection.current.close();
      globalWsConnection.current = null;
    }
    if (globalWsReconnectTimeout.current) {
      clearTimeout(globalWsReconnectTimeout.current);
      globalWsReconnectTimeout.current = null;
    }
  };

  const setCurrentPublicationId = (publicationId) => {
    console.log(`[WebSocket] Setting current publication ID to: ${publicationId}`);
    currentPublicationIdRef.current = publicationId;
  };

  const isTaskForCurrentPublication = (taskType, publicationId) => {
    if (!activeTasksState[taskType] || activeTasksState[taskType].length === 0) return false;

    // Check if any active project ID belongs to this publication
    return activeTasksState[taskType].some(id => {
      if (id === 'legacy') {
        const task = taskStatus[taskType];
        return task && Number(task.publicationId) === Number(publicationId);
      }
      const task = taskStatus[taskType]?.[id];
      return task && Number(task.publicationId) === Number(publicationId);
    });
  };

  const getActiveTaskProgress = (taskType, publicationId) => {
    if (!activeTasksState[taskType] || activeTasksState[taskType].length === 0) return { progress: 0, status: "", id: null };

    // Status rank — higher = more advanced / more important to surface
    const STATUS_RANK = {
      TemplatesSelected: 7,
      completed: 6,
      processing: 5,
      merging: 5,
      generating: 4,
      queued: 3,
      failed: 2,
      error: 2,
    };

    // Collect ALL matching IDs for this publication, then pick the most-advanced one.
    let bestId = null;
    let bestRank = -1;
    let bestProgress = -1;

    for (const id of activeTasksState[taskType]) {
      if (id === 'legacy') continue;
      const task = taskStatus[taskType]?.[id];
      if (!task || Number(task.publicationId) !== Number(publicationId)) continue;
      const rank = STATUS_RANK[task.status] ?? 0;
      const prog = task.progress ?? 0;
      if (rank > bestRank || (rank === bestRank && prog > bestProgress)) {
        bestId = id;
        bestRank = rank;
        bestProgress = prog;
      }
    }

    if (bestId) {
      return {
        progress: taskStatus[taskType][bestId].progress || 0,
        status: taskStatus[taskType][bestId].status || "",
        id: bestId,
      };
    }

    // Fallback to legacy structure check
    if (activeTasksState[taskType].includes('legacy')) {
      const legacyTask = taskStatus[taskType];
      if (legacyTask && Number(legacyTask.publicationId) === Number(publicationId)) {
        return { progress: legacyTask.progress || 0, status: legacyTask.status || "", id: 'legacy' };
      }
    }

    return { progress: 0, status: "", id: null };
  };

  return (
    <WebSocketContext.Provider value={{
      activeTasks: activeTasksState,
      setActiveTasks,
      taskStatus,
      setTaskStatus,
      setupWebSocket,
      setCurrentPublicationId,
      isTaskForCurrentPublication,
      getActiveTaskProgress,
      connectGlobalWebSocket,
      disconnectGlobalWebSocket,
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSockets = () => useContext(WebSocketContext);